// Lookup and board mechanics for /ielts/calculator.
//
// ⛔ No arithmetic here. The server computes every position of the fader under
// both schedules before the page is sent — 16 positions × 2 schedules — and
// this file picks a row out of what it sent. A second implementation of the
// engine in JavaScript would drift from the Ruby one, and the only way to catch
// that would be a fixture comparing the two, which is machinery this avoids.

/**
 * @param {Array<Object>} variants rows the server precomputed
 * @param {string} schedule "part-time" or "full-time"
 * @param {number} dailyHours the fader position
 * @returns {Object|undefined} the row, or undefined when the server sent none
 */
export function selectVariant (variants, schedule, dailyHours) {
  if (!Array.isArray(variants)) return undefined

  return variants.find(
    (variant) => variant.schedule === schedule && Number(variant.dailyHours) === Number(dailyHours)
  )
}

/**
 * One decimal place, with a trailing zero dropped: the board reads "6.5 小時"
 * and "3 小時", never "3.0 小時".
 *
 * ⛔ A value that is not a number comes back empty rather than as "NaN": a
 * missing figure must leave a blank, not a word the reader has to decode.
 */
export function formatNumber (value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return ''

  return String(Math.round(Number(value) * 10) / 10)
}

/**
 * The split-flap stagger: which tile turns over at which moment.
 *
 * A real board does not turn its whole row at once — the leaves run left to
 * right, and the row is legible again the instant the last one lands. Returns a
 * delay in milliseconds per character index.
 *
 * ⛔ Bounded. A long value must not take longer to settle than a short one by
 * more than the cap, or the board feels broken rather than mechanical.
 */
export function flapDelays (length, { step = 45, cap = 360 } = {}) {
  if (!Number.isFinite(length) || length <= 0) return []

  const spacing = length * step > cap ? cap / length : step

  return Array.from({ length }, (_, index) => Math.round(index * spacing))
}

/**
 * Which characters actually changed between the old value and the new one.
 *
 * ⛔ Only those tiles turn. Flipping a leaf that already shows the right
 * character is noise, and it is exactly what makes a rebuilt board look like an
 * animation rather than a mechanism.
 */
export function changedIndexes (from, to) {
  const before = String(from ?? '')
  const after = String(to ?? '')
  const length = Math.max(before.length, after.length)
  const changed = []

  for (let index = 0; index < length; index += 1) {
    if (before[index] !== after[index]) changed.push(index)
  }

  return changed
}

/**
 * A board has a fixed number of leaves per field, so a shorter value arrives
 * padded rather than shrinking the row: 6 weeks reads 06, which is what a real
 * board shows and what keeps the leaves turning instead of being rewritten.
 *
 * ⛔ Only pads a plain integer. A value that genuinely needs more leaves than
 * the row has (or that carries a decimal point) is written straight in.
 */
export function fitToLeaves (value, leaves) {
  const text = String(value)

  if (text.length >= leaves) return text
  if (!/^\d+$/.test(text)) return text

  return text.padStart(leaves, '0')
}
